import { create } from 'zustand';
import type { ConversaMessage, ConversationType } from '@/types/conversa.types';
import { conversationsApi } from '@/services/api.service';

interface ConversaState {
  messages: ConversaMessage[];
  isLoading: boolean;
  roomCode: string | null;
  addMessage: (message: ConversaMessage) => void;
  addChatMessage: (senderName: string, text: string, persist?: boolean) => void;
  addTranscriptionMessage: (participantName: string, text: string, persist?: boolean) => void;
  loadHistory: (roomCode: string) => Promise<void>;
  setRoomCode: (roomCode: string) => void;
  clearMessages: () => void;
}

export const useConversaStore = create<ConversaState>((set, get) => ({
  messages: [],
  isLoading: false,
  roomCode: null,

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  addChatMessage: (senderName, text, persist = true) => {
    const message: ConversaMessage = {
      id: `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'chat',
      senderName,
      text,
      timestamp: new Date(),
    };

    set((state) => ({
      messages: [...state.messages, message],
    }));

    // Persist to backend
    const { roomCode } = get();
    if (persist && roomCode) {
      conversationsApi.saveMessage(roomCode, {
        message_type: 'chat',
        sender_name: senderName,
        text,
      }).catch((err) => {
        console.error('Failed to persist chat message:', err);
      });
    }
  },

  addTranscriptionMessage: (participantName, text, persist = true) => {
    const message: ConversaMessage = {
      id: `trans-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'transcription',
      senderName: participantName,
      text,
      timestamp: new Date(),
    };

    set((state) => ({
      messages: [...state.messages, message],
    }));

    // Persist to backend
    const { roomCode } = get();
    if (persist && roomCode) {
      conversationsApi.saveMessage(roomCode, {
        message_type: 'transcription',
        sender_name: participantName,
        text,
      }).catch((err) => {
        console.error('Failed to persist transcription message:', err);
      });
    }
  },

  loadHistory: async (roomCode: string) => {
    set({ isLoading: true, roomCode });

    try {
      const response = await conversationsApi.getHistory(roomCode);

      // Convert API response to ConversaMessage format
      const messages: ConversaMessage[] = response.messages.map((msg) => ({
        id: `db-${msg.id}`,
        type: msg.messageType as ConversationType,
        senderName: msg.senderName,
        text: msg.text,
        timestamp: new Date(msg.createdAt),
      }));

      set({ messages, isLoading: false });
      console.log(`Loaded ${messages.length} messages from history for room ${roomCode}`);
    } catch (error) {
      console.error('Failed to load conversation history:', error);
      set({ isLoading: false });
    }
  },

  setRoomCode: (roomCode) => set({ roomCode }),

  clearMessages: () => set({ messages: [], roomCode: null }),
}));
